/**
 * TanStack Query hooks for the "Offene Prüfungen" dashboard view (TES-506).
 *
 * Reales Schema ist Gesetz (D-CHK-015) — every column verified against
 * `src/integrations/supabase/types.ts`:
 *  - `inspections`: status, due_date, assigned_to, position_id, checklist_id,
 *    assigned_group_id (nullable), tenant_id (nullable), created_at.
 *  - joins → `inspection_positions.name`, `checklists.name`, `team_group.name`, `profiles`.
 *  - `pdf_profiles`: name, description, protocol_type, is_default.
 *
 * TES-2017 (v2.3): grouping axis is the Fachbereich-**Gruppe** (assigned_group_id →
 * `team_group.name`), not the Team — the "Offen nach Gruppe" dashboard card + filter.
 *
 * All reads/writes go through the user-JWT-scoped client (`supabase`) so RLS +
 * RBAC gate every row. NEVER service_role (CLAUDE.md). Explicit columns only.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import type { Database } from "@/integrations/supabase/types";
import { OPEN_INSPECTION_STATUSES, type InspectionStatus } from "@/lib/openInspectionsWindow";

export interface OpenInspectionProfile {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
}

export interface OpenInspectionRow {
  id: string;
  status: InspectionStatus;
  due_date: string | null;
  created_at: string;
  assigned_to: string | null;
  position_id: string | null;
  checklist_id: string;
  group_id: string | null;
  position_name: string | null;
  checklist_name: string | null;
  group_name: string | null;
  assigned_profile: OpenInspectionProfile | null;
}

export type PdfProtocolType = Database["public"]["Enums"]["pdf_protocol_type"];

export interface PdfProfileRow {
  id: string;
  name: string;
  description: string | null;
  protocol_type: PdfProtocolType;
  is_default: boolean;
}

const openInspectionsKeys = {
  all: ["open-inspections"] as const,
  list: (params: { userId?: string; canSeeAll: boolean }) =>
    [...openInspectionsKeys.all, "list", params] as const,
  pdfProfiles: ["open-inspections", "pdf-profiles"] as const,
};

/**
 * Fetch all currently-open inspections (status pending/in_progress/overdue,
 * `cancelled`/`completed` excluded server-side). Date-window/team/status/
 * checklist filtering for the cards + table happens client-side on this set,
 * so a single cached query feeds the whole panel.
 *
 * RLS gates visibility by team/tenant; the optional `assigned_to` narrowing is
 * a UX scope for non-privileged roles, NOT a security boundary.
 */
async function fetchOpenInspections(
  userId: string | undefined,
  canSeeAll: boolean,
): Promise<OpenInspectionRow[]> {
  if (!userId) return [];

  let query = supabase
    .from("inspections")
    .select(
      `
      id,
      status,
      due_date,
      created_at,
      assigned_to,
      position_id,
      checklist_id,
      assigned_group_id,
      inspection_positions ( name ),
      checklists ( name )
    `,
    )
    .in("status", [...OPEN_INSPECTION_STATUSES])
    .order("due_date", { ascending: true, nullsFirst: false });

  if (!canSeeAll) {
    query = query.eq("assigned_to", userId);
  }

  const { data, error } = await query;
  if (error) throw error;

  const rows = data ?? [];

  // Resolve group (Fachbereich) names + assignee profiles in parallel (explicit columns).
  // Group names come from `team_group` — RLS gates which groups are visible (same
  // pattern as useTeamGroups/useDeputyPrincipals); unresolved ids fall back to the id.
  const groupIds = [...new Set(rows.map((r) => r.assigned_group_id).filter(Boolean))] as string[];
  const assigneeIds = [...new Set(rows.map((r) => r.assigned_to).filter(Boolean))] as string[];

  const [groupsResult, profilesResult] = await Promise.all([
    groupIds.length > 0
      ? supabase.from("team_group").select("id, name").in("id", groupIds)
      : Promise.resolve({ data: [], error: null }),
    assigneeIds.length > 0
      ? supabase
          .from("profiles")
          .select("user_id, first_name, last_name, display_name")
          .in("user_id", assigneeIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  const groupMap = new Map<string, string>();
  (groupsResult.data ?? []).forEach((g) => groupMap.set(g.id, g.name));

  const profileMap = new Map<string, OpenInspectionProfile>();
  (profilesResult.data ?? []).forEach((p) => profileMap.set(p.user_id, p));

  return rows.map((r) => ({
    id: r.id,
    status: r.status,
    due_date: r.due_date,
    created_at: r.created_at,
    assigned_to: r.assigned_to,
    position_id: r.position_id,
    checklist_id: r.checklist_id,
    group_id: r.assigned_group_id,
    position_name: r.inspection_positions?.name ?? null,
    checklist_name: r.checklists?.name ?? null,
    group_name: r.assigned_group_id ? (groupMap.get(r.assigned_group_id) ?? null) : null,
    assigned_profile: r.assigned_to ? (profileMap.get(r.assigned_to) ?? null) : null,
  }));
}

export function useOpenInspectionsQuery(enabled = true) {
  const { user, isAdmin, isQualitaetsmanager } = useAuth();
  const canSeeAll = isAdmin || isQualitaetsmanager;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: openInspectionsKeys.list({ userId: user?.id, canSeeAll }),
    queryFn: () => fetchOpenInspections(user?.id, canSeeAll),
    enabled: !!user?.id && enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });

  useRealtimeSubscription(
    ["inspections"],
    () => queryClient.invalidateQueries({ queryKey: openInspectionsKeys.all }),
    { enabled: !!user },
  );

  return {
    inspections: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Assign (or unassign) an inspection to a user. Runs through the user-JWT
 * client so RLS gates the row. Invalidates the open-inspections cache on success.
 */
export function useAssignInspectionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      inspectionId,
      assignedTo,
    }: {
      inspectionId: string;
      assignedTo: string | null;
    }) => {
      const { error } = await supabase
        .from("inspections")
        .update({ assigned_to: assignedTo, updated_at: new Date().toISOString() })
        .eq("id", inspectionId)
        .select("id")
        .single();
      if (error) throw error;
      return inspectionId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: openInspectionsKeys.all });
    },
  });
}

/**
 * Fetch the available PDF protocol templates (`pdf_profiles`). RLS gates by
 * tenant. No approval/Freigabe column exists (GAP 2) — this is config only.
 */
async function fetchPdfProfiles(): Promise<PdfProfileRow[]> {
  const { data, error } = await supabase
    .from("pdf_profiles")
    .select("id, name, description, protocol_type, is_default")
    .order("is_default", { ascending: false })
    .order("name", { ascending: true });
  if (error) throw error;
  return (data ?? []) as PdfProfileRow[];
}

export function usePdfProfilesQuery(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: openInspectionsKeys.pdfProfiles,
    queryFn: fetchPdfProfiles,
    enabled: !!user && enabled,
    staleTime: 5 * 60 * 1000,
  });
}
